import {
  ItemTimeslotsTypeEnum,
  type GetItemTimeslots200ResponseInner,
  type ItemTimeslots,
  type SessionFormAssessmentResponse,
} from "@simbelapp/order-sdk";
import type { ISessionStepEntry } from "~/utils/interfaces/evaluations-interfaces";

type TrainingStep = Extract<GetItemTimeslots200ResponseInner, { type: "TRAINING" }>;

const isTrainingStep = (step: GetItemTimeslots200ResponseInner): step is TrainingStep =>
  step.type === ItemTimeslotsTypeEnum.Training;

/**
 * A session's timeslots route mixes training slots and assessment placements. Everything that schedules, signs or
 * counts hours reads the training slots alone.
 */
export function splitSessionSteps(steps: GetItemTimeslots200ResponseInner[]): {
  trainings: ItemTimeslots[];
  assessments: SessionFormAssessmentResponse[];
} {
  const trainings: ItemTimeslots[] = [];
  const assessments: SessionFormAssessmentResponse[] = [];
  for (const step of steps) {
    if (isTrainingStep(step)) trainings.push(step);
    else assessments.push(step);
  }
  return { trainings, assessments };
}

const dayOf = (date?: string | null): string | null => (date ? date.slice(0, 10) : null);

/**
 * A session's « Étapes »: the training steps in their own order and numbering (« Étape n »), each questionnaire
 * placed after the steps starting on or before its date. An undated step moves no questionnaire.
 */
export function interleaveSessionSteps<T, Q>(
  timeslots: T[],
  timeslotStart: (timeslot: T) => string | null | undefined,
  questionnaires: Q[],
  questionnaireDate: (questionnaire: Q) => string | null | undefined,
): ISessionStepEntry<T, Q>[] {
  const pending = [...questionnaires].sort((a, b) =>
    (dayOf(questionnaireDate(a)) ?? "").localeCompare(dayOf(questionnaireDate(b)) ?? ""),
  );
  const entries: ISessionStepEntry<T, Q>[] = [];
  const pushQuestionnaire = (questionnaire: Q) => entries.push({ timeslot: null, timeslotIndex: -1, questionnaire });

  timeslots.forEach((timeslot, timeslotIndex) => {
    const start = dayOf(timeslotStart(timeslot));
    while (pending.length && start && (dayOf(questionnaireDate(pending[0]!)) ?? "") < start) {
      pushQuestionnaire(pending.shift()!);
    }
    entries.push({ timeslot, timeslotIndex, questionnaire: null });
  });
  pending.forEach(pushQuestionnaire);
  return entries;
}
